import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import type { EntryComponent } from 'cms/entries/entries';
import type {
  IFcTestimonialCarouselCard,
  ILpComponentPhotoTestimonials,
} from 'declarations/contentful';
import Head from 'next/head';

import Grid from './Grid';
import { resizeTestimonialAvatarImageUrl } from './shared/utils';

const DESKTOP_CARD_ROW_MEDIA = '(min-width: 1201px)';

const getAvatarImageUrl = (entry: IFcTestimonialCarouselCard) => {
  const imageUrl = entry.fields.image?.fields?.file?.url;
  return imageUrl ? resizeTestimonialAvatarImageUrl(imageUrl) : null;
};

const TestimonialCard = ({ entry }: { entry: IFcTestimonialCarouselCard }) => {
  const { fields } = entry;
  const resizedImageUrl = getAvatarImageUrl(entry);

  return (
    <Col className="flex-1 items-center gap-6 rounded-4xl bg-od-bg-tertiary p-9 max-md:p-6">
      {resizedImageUrl && (
        <Box className="h-28 w-28 overflow-hidden rounded-full max-md:h-20 max-md:w-20">
          <Image
            src={resizedImageUrl}
            alt={fields.name}
            width={112}
            height={112}
            className="h-28 w-28 object-cover max-md:h-20 max-md:w-20"
          />
        </Box>
      )}
      <Text as="p" className="text-center text-body-md text-od-content-primary">
        &ldquo;{fields.quote}&rdquo;
      </Text>
      <Col className="items-center gap-1">
        <Text as="p" className="text-label-lg text-od-content-primary">
          {fields.name}
        </Text>
        <Text as="p" className="text-label-sm text-od-content-tertiary">
          {fields.locationContext}
        </Text>
      </Col>
    </Col>
  );
};

const RenderPhotoTestimonials = (entry: ILpComponentPhotoTestimonials) => {
  const { fields } = entry;
  const cards = fields.cards ?? [];
  const avatarImageUrls = Array.from(
    new Set(cards.map(getAvatarImageUrl).filter((url): url is string => Boolean(url))),
  );

  return (
    <Col
      analyticsName={`cosmos-landing-page-photo-testimonials-${
        fields?.analyticsId ?? entry.sys.id
      }`}
      trackImpression
    >
      <Head>
        {avatarImageUrls.map((url, index) => (
          <link
            key={`photo-testimonials-avatar-${url}`}
            rel="preload"
            as="image"
            href={url}
            media={index === 0 ? undefined : DESKTOP_CARD_ROW_MEDIA}
            fetchPriority="high"
          />
        ))}
      </Head>
      <Grid>
        <Col className="col-span-12 gap-10 py-16 max-md:py-8">
          <Col className="gap-3">
            <Text as="p" className="text-label-sm text-od-content-accent-brand">
              {fields.eyebrow}
            </Text>
            <Col className="gap-2">
              <Text as="h2" className="text-header-sm text-od-content-primary">
                {fields.counterTarget
                  ? `${fields.counterTarget.toLocaleString('en-US')} ${fields.title}`
                  : fields.title}
              </Text>
              <Text as="p" className="text-body-md text-od-content-tertiary">
                {fields.description}
              </Text>
            </Col>
          </Col>

          <Row className="gap-5 max-lg:flex-col">
            {cards.map((card: IFcTestimonialCarouselCard) => (
              <Box key={card.sys.id} className="flex-1 lg:min-w-70">
                <TestimonialCard entry={card} />
              </Box>
            ))}
          </Row>
        </Col>
      </Grid>
    </Col>
  );
};

const PhotoTestimonials: EntryComponent<ILpComponentPhotoTestimonials> = {
  render: RenderPhotoTestimonials,
};

export default PhotoTestimonials;
